import React, { useState, useEffect, useRef } from 'react';

const ObsessiveDisorder = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [symptomsCanScrollLeft, setSymptomsCanScrollLeft] = useState(false);
  const [symptomsCanScrollRight, setSymptomsCanScrollRight] = useState(false);
  const [treatmentCanScrollLeft, setTreatmentCanScrollLeft] = useState(false);
  const [treatmentCanScrollRight, setTreatmentCanScrollRight] = useState(false);
  const symptomsScrollRef = useRef(null);
  const causesScrollRef = useRef(null);
  const typesScrollRef = useRef(null);
  const treatmentScrollRef = useRef(null);

  const updateScrollButtons = (ref, setCanLeft, setCanRight) => {
    if (!ref.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = ref.current;
    const maxScrollLeft = scrollWidth - clientWidth;

    setCanLeft(scrollLeft > 0);
    setCanRight(scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['overview', 'symptoms', 'causes', 'types', 'treatment'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          const sectionBottom = offsetTop + offsetHeight;
          
          // More precise boundary detection with smaller overlap
          if (scrollPosition >= offsetTop && scrollPosition < sectionBottom - 50) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    const t = window.setTimeout(() => {
      updateScrollButtons(symptomsScrollRef, setSymptomsCanScrollLeft, setSymptomsCanScrollRight);
      updateScrollButtons(treatmentScrollRef, setTreatmentCanScrollLeft, setTreatmentCanScrollRight);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollTypes = (direction) => {
    const scrollAmount = 320;
    if (typesScrollRef.current) {
      typesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollCauses = (direction) => {
    const scrollAmount = 320;
    if (causesScrollRef.current) {
      causesScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollSymptoms = (direction) => {
    const scrollAmount = 320;
    if (symptomsScrollRef.current) {
      symptomsScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      window.setTimeout(() => {
        updateScrollButtons(symptomsScrollRef, setSymptomsCanScrollLeft, setSymptomsCanScrollRight);
      }, 350);
    }
  };

  const scrollTreatment = (direction) => {
    const scrollAmount = 320;
    if (treatmentScrollRef.current) {
      treatmentScrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      window.setTimeout(() => {
        updateScrollButtons(treatmentScrollRef, setTreatmentCanScrollLeft, setTreatmentCanScrollRight);
      }, 350);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[#E6F7F4]"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
          <div className="relative overflow-hidden rounded-3xl bg-[#E6F7F4]">
            <div className="relative px-6 py-24 sm:px-10 sm:py-32 lg:px-12 lg:py-36 text-center">
              <img
                src="/static/img/services/ocd.webp"
                alt=""
                className="pointer-events-none absolute -right-2 top-1/2 hidden w-72 -translate-y-1/2 opacity-75 lg:block"
              />

              <div className="mx-auto flex items-center justify-center gap-2 text-xs tracking-wide text-gray-700">
                <span>Adult Services</span>
              </div>

              <h1 className="mt-6 font-extrabold tracking-tight text-gray-900 text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
                Obsessive Disorder<br/> care
              </h1>
    
              <p className="mt-6 text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
                Specialized support for OCD and related disorders. Evidence-based therapies, exposure techniques, and personalized care for managing intrusive thoughts and compulsions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-20 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center py-4">
            <div className="flex flex-wrap items-center gap-6">
              <a 
                href="#overview" 
                className={`font-medium transition-colors ${
                  activeSection === 'overview' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('overview');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                  setTimeout(() => {
                    setActiveSection('overview');
                  }, 800);
                }}
              >
                Overview
              </a>
              <a 
                href="#symptoms" 
                className={`font-medium transition-colors ${
                  activeSection === 'symptoms' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('symptoms');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                  setTimeout(() => {
                    setActiveSection('symptoms');
                  }, 800);
                }}
              >
                Symptoms
              </a>
              <a 
                href="#causes" 
                className={`font-medium transition-colors ${
                  activeSection === 'causes' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('causes');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                  setTimeout(() => {
                    setActiveSection('causes');
                  }, 800);
                }}
              >
                Causes and Risk
              </a>
              <a 
                href="#types" 
                className={`font-medium transition-colors ${
                  activeSection === 'types' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('types');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                  // Force update active section after scroll completes
                  setTimeout(() => {
                    setActiveSection('types');
                  }, 800);
                }}
              >
                Types
              </a>
              <a 
                href="#treatment" 
                className={`font-medium transition-colors ${
                  activeSection === 'treatment' 
                    ? 'text-[#00A785]' 
                    : 'text-gray-700 hover:text-[#00A785]'
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('treatment');
                  const offset = 200;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                  });
                  setTimeout(() => {
                    setActiveSection('treatment');
                  }, 800);
                }}
              >
                Treatment
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Overview */}
          <div id="overview" className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Overview</h2>
            <p className="text-lg text-gray-600 mb-8">
              Obsessive-Compulsive Disorder (OCD) is a mental health condition characterized by unwanted, intrusive thoughts (obsessions) and repetitive behaviors or mental acts (compulsions) that a person feels driven to perform.
              These obsessions and compulsions can significantly interfere with daily activities, work, school, and personal relationships.
            </p>
            <p className="text-lg text-gray-600 mb-8">
              People with OCD may recognize that their obsessions and compulsions are excessive or unreasonable, but they feel unable to control them. 
              The condition typically follows a chronic course with periods of improvement and worsening, but effective treatments are available to help manage symptoms and improve quality of life.
            </p>
          </div>
        </div>

        {/* Symptoms Section with Full-Width Background */}
        <div id="symptoms" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Symptoms</h2>
            
            {/* Common Signs */}
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Recognizing the signs of obsessive-compulsive disorder</h3>
              
              {/* Scroll Container */}
              <div 
                ref={symptomsScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
                onScroll={() =>
                  updateScrollButtons(symptomsScrollRef, setSymptomsCanScrollLeft, setSymptomsCanScrollRight)
                }
              >
                <div className="flex space-x-4 py-4 w-fit">
                  {[
                    {
                      symptom: "Fear of Contamination by Germs or Dirt",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Unwanted Taboo Thoughts about Sexual or Religious Subjects",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Needing Things to be Orderly and Symmetrical",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Aggressive Thoughts About Losing Control and Harming Oneself or Others",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Cleaning and Qashing Compulsions (e.g., Excessive Handwashing)",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Checking Compulsions (e.g., Doors, Stove, Locks)",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Repeating, Counting, and Orderliness Compulsions",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                      )
                    },
                    {
                      symptom: "Following Strict Routines and Demanding Reassurance",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.symptom}</h4>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll Buttons */}
              <div className="flex justify-center space-x-2">
                <button
                  onClick={() => scrollSymptoms('left')}
                  disabled={!symptomsCanScrollLeft}
                  className={`bg-white border border-gray-200 rounded-lg p-3 shadow-lg transition-shadow ${
                    symptomsCanScrollLeft
                      ? 'hover:shadow-xl'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => scrollSymptoms('right')}
                  disabled={!symptomsCanScrollRight}
                  className={`bg-white border border-gray-200 rounded-lg p-3 shadow-lg transition-shadow ${
                    symptomsCanScrollRight
                      ? 'hover:shadow-xl'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Hide Scrollbar Styles */}
        <style jsx>{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>

        {/* Causes Section with Full-Width Background */}
        <div id="causes" className="bg-gray-100 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Causes and Risk Factors</h2>
            
            {/* Possible Causes */}
            <div>
              <h3 className="text-lg font-normal text-gray-700 mb-6">Understanding factors that contribute to obsessive-compulsive disorder</h3>
              
              {/* Grid Container - Single Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Genetic Predisposition",
                    description: "Having parents or other family members with OCD can increase your risk of developing the disorder.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Learning and Observation",
                    description: "Obsessive fears and compulsive behaviors can be learned from watching family members or personal experiences.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    )
                  },
                  {
                    title: "Stressful Life Events",
                    description: "Traumatic or stressful events, especially during childhood, can increase the risk of developing OCD.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    )
                  },
                  {
                    title: "Related Mental Health Disorders",
                    description: "OCD may be related to anxiety disorders, depression, substance abuse, or tic disorders.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Types Section with Full-Width Background */}
        <div id="types" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-[#00A785] mb-4">Types of OCD</h2>
            
            <div>
              <h3 className="text-lg font-normal text-gray-600 mb-6">Different manifestations of obsessive-compulsive disorder</h3>
              
              {/* Grid Container - Single Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  {
                    title: "Contamination OCD",
                    description: "Fear of germs, dirt, or contamination leading to excessive cleaning rituals.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )
                  },
                  {
                    title: "Checking OCD",
                    description: "Repeated checking of locks, appliances, or safety concerns to prevent harm.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Symmetry and Ordering",
                    description: "Need for objects to be arranged perfectly and intense distress at disorder.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Intrusive Thoughts",
                    description: "Unwanted violent, sexual, or religious thoughts causing significant distress.",
                    icon: (
                      <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Treatment Section with Full-Width Background */}
        <div id="treatment" className="py-16 bg-[#00A785]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Treatment</h2>
            
            <div>
              <h3 className="text-lg font-normal text-white mb-6">Evidence-based approaches to managing obsessive-compulsive disorder</h3>
              
              {/* Scroll Container */}
              <div 
                ref={treatmentScrollRef}
                className="overflow-x-auto scrollbar-hide mb-4"
                onScroll={() =>
                  updateScrollButtons(treatmentScrollRef, setTreatmentCanScrollLeft, setTreatmentCanScrollRight)
                }
              >
                <div className="flex space-x-4 py-4 w-fit">
                  {[
                    {
                      title: "Medication Management",
                      description: "SSRIs and other antidepressant medications to reduce OCD symptoms and improve functioning.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      )
                    },
                    {
                      title: "Cognitive Behavioral Therapy",
                      description: "CBT helps identify triggers, challenge cognitive distortions, and develop personal coping strategies.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )
                    },
                    {
                      title: "Exposure and Response Prevention",
                      description: "Systematic exposure to triggers while learning not to respond with compulsive behaviors.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      )
                    },
                    {
                      title: "Relaxation and Mindfulness",
                      description: "Meditation, yoga, breathing exercises, and mindfulness to reduce stress and anxiety.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )
                    },
                    {
                      title: "Social and Community Support",
                      description: "Support groups for people with OCD to share experiences and find understanding.",
                      icon: (
                        <svg className="w-6 h-6 text-green-500 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                      )
                    }
                  ].map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow flex-shrink-0 w-[300px]">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          {item.icon}
                        </div>
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Scroll Buttons */}
              <div className="flex justify-center space-x-2">
                <button
                  onClick={() => scrollTreatment('left')}
                  disabled={!treatmentCanScrollLeft}
                  className={`bg-white border border-gray-200 rounded-lg p-3 shadow-lg transition-shadow ${
                    treatmentCanScrollLeft
                      ? 'hover:shadow-xl'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => scrollTreatment('right')}
                  disabled={!treatmentCanScrollRight}
                  className={`bg-white border border-gray-200 rounded-lg p-3 shadow-lg transition-shadow ${
                    treatmentCanScrollRight
                      ? 'hover:shadow-xl'
                      : 'opacity-50 cursor-not-allowed'
                  }`}
                >
                  <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default ObsessiveDisorder;
